import React, { ReactElement, createContext, useCallback, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { addBreadcrumb } from "@sentry/react";
// import apiClient from "utilities/api/apiClient";
import getToastChits from "utilities/getToastChits";
import { queryKeys } from "utilities/constants";
import { updateToastChits } from "utilities/updateToastChits";
import useApi from "hooks/useApi";
import { useLocation } from "./LocationContext";
import { useLocationFlags } from "./LocationFlagsContext";
import { useNotifications } from "./NotificationsContext";

export interface ToastChitFlags {
  guestNoteEnabled: boolean;
  reservationNoteEnabled: boolean;
  allergiesEnabled: boolean;  
  specialOccasionEnabled: boolean;
}

export interface UpdateToastChitsPayload {
  note: string;
  flags: ToastChitFlags;
}

export interface GDAContextProps {
  isGDAEnabled: boolean;
  setIsGDAEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  handleUpdateGDAEnabled: (checked: boolean) => void;
  isToastChitsEnabled?: boolean;
  isToastChitsLoading: boolean;
  toastChits?: string;
  handleUpdateToastChits?: (payload: UpdateToastChitsPayload) => Promise<void>;
  toastChitsFlags: ToastChitFlags;
}

export const GDAContext = createContext<GDAContextProps | null>(null);
GDAContext.displayName = "GDAContext";

export function GDAProvider(props:any): ReactElement {
    const [isGDAEnabled, setIsGDAEnabled] = React.useState<boolean>(false);
    const [isToastChitsEnabled, setIsToastChitsEnabled] = React.useState<boolean>(false);
    const [toastChits, setToastChits] = React.useState<string | undefined>(undefined);
    const [toastChitsFlags, setToastChitsFlags] = React.useState<ToastChitFlags>({
      guestNoteEnabled: false,
      reservationNoteEnabled: false,
      allergiesEnabled: false,
      specialOccasionEnabled: false,
    });

    const { currentLocation, partnerId } = useLocation();
    const locationId = currentLocation?.locationId;

    const { checkIfFlagIsEnabled, locationFlags } = useLocationFlags();

    const apiClient = useApi();

    const { notify } = useNotifications();

    const queryClient = useQueryClient();

    const handleUpdateGDAEnabled = (checked: boolean) => {
        setIsGDAEnabled(checked);
    };

    const { 
      isLoading: isToastChitsLoading,
      refetch: refetchToastChits 
    } = useQuery(
      queryKeys.toastChits,
        async () => 
            await getToastChits({
              apiClient,
              locationId,
              partnerId,
            }),
        {
          enabled: !!locationId && !!partnerId,
          async onSuccess(data: any) {
            setToastChits(data.resource?.note);
            setToastChitsFlags({
              guestNoteEnabled: data.resource?.guestNoteEnabled ?? false,
              reservationNoteEnabled: data.resource?.reservationNoteEnabled ?? false,
              allergiesEnabled: data.resource?.allergiesEnabled ?? false,
              specialOccasionEnabled: data.resource?.specialOccasionEnabled ?? false,
            });
          },
          onError: (error: any) => {
            notify({
              type: "error",
              message: `Failed to fetch Toast Chits data: ${error?.message}`,       
            });
            
            console.error("Failed to fetch Toast Chits data:", error);
          },
        }
    );

    const updateToastChitsMutation = useMutation(
      async ({ note, flags }: UpdateToastChitsPayload) => {
        if (!locationId || !partnerId) {
          throw new Error("updateToastChitsMutation: Location ID and Partner ID are required to update Toast Chits data.");
        }

        addBreadcrumb({
          level: "info",
          category: "user_action",
          message: `Update toastChits for locationId:${currentLocation?.id}`,
        });

        const response = await updateToastChits({
          apiClient,
          locationId,
          note,
          flags,
          partnerId,
        });

        return response;
      },
      {
        onSuccess: async (_data, variables) => {
          notify({
            type: "success",
            message: "GDA Toast Chits updated successfully.",
          });

          // Keep UI stable by applying the saved values immediately.
          setToastChits(variables.note);
          setToastChitsFlags(variables.flags);

          await refetchToastChits();
        },
        onError: (error: any) => {
          notify({
            type: "error",
            message: `Failed to update GDA Toast Chits: ${error?.message}`,
          });

          console.error("Failed to update GDA Toast Chits:", error);
        },
      },
    );

    const handleUpdateToastChits = useCallback(
      async (payload: UpdateToastChitsPayload) => {
        await updateToastChitsMutation.mutateAsync(payload);
      },
      [updateToastChitsMutation],
    );

    useEffect(() => {
      if (locationFlags) {
        setIsGDAEnabled(checkIfFlagIsEnabled("gda.enabled"));
        setIsToastChitsEnabled(checkIfFlagIsEnabled("toast-chits.enabled"));
      }
    }, [checkIfFlagIsEnabled, locationFlags]);
    

    // reset queries on location change
    useEffect(() => {
        if (currentLocation?.id) {
          queryClient.setQueryData(queryKeys.gda, undefined);
          queryClient.setQueryData(queryKeys.toastChits, undefined);

          setIsGDAEnabled(false);
          setIsToastChitsEnabled(false);
          setToastChits(undefined);

          // void refetchGDAFlags();
          void refetchToastChits();
        }
    }, [currentLocation?.id, queryClient, refetchToastChits]);

    const value: GDAContextProps = {
      isGDAEnabled,
      setIsGDAEnabled,
      handleUpdateGDAEnabled,
      isToastChitsEnabled,
      isToastChitsLoading,
      toastChits,
      handleUpdateToastChits,
      toastChitsFlags,
    };

    return (<GDAContext.Provider value={value} {...props} />) as ReactElement;
};

export function useGDA() {
    const context = React.useContext(GDAContext);

    if (context === undefined) {
      throw new Error("useGDA must be used within a GDAProvider");
    }

    // Because we are getting the context from GDAProvider here, if context is
    // not 'undefined' (ie: the GDAProvider is not a parent of this component),
    // we can be sure context is not 'null' here
    if (context === null) {
      throw new Error("GDAProvider supplied null context");
    }

    return context;
};